import {
    SidebarGroup,
    SidebarGroupContent,
    SidebarMenu,
    SidebarMenuBadge,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    Button,
} from '@duck/ui'
import React, { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Home, Trophy, Activity, Info, HelpCircle, User, ClipboardList, Megaphone } from 'lucide-react'
import { useTar } from '@/modules/i18n/useTar'
import { useOngoingChallenges } from '@/hooks/useOngoingChallenges'
import { useReleaseNotes } from '@/hooks/useReleaseNotes'
import { useShowJoinGamma } from '@/hooks/useShowJoinGamma'
import { SideBarAdmin } from './SideBarAdmin'
import { SideBarPartner } from './SideBarPartner'
import { useAuth } from '@/hooks/useAuth'
import { UserService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'
import { toast } from 'sonner'
import { useDebouncedEffect } from '@/hooks/useDebouncedEffect'

type SideBarMenuProps = {
    open: boolean
    handleMobileNav: () => void
}

export const SideBarMenu = ({ open, handleMobileNav }: SideBarMenuProps) => {
    const { t } = useTar()
    const { challenges: ongoingChallenges, assignments: activeAssignments } = useOngoingChallenges()
    const { isAdmin, isPartner, isPerson, isAuthenticated } = useAuth()
    const { showJoinGammaCta } = useShowJoinGamma()
    const { unreadCount, fetchNotes, isBlocked: releaseNotesBlocked } = useReleaseNotes(true, false)
    const navigate = useNavigate()
    const [isJoiningGamma, setIsJoiningGamma] = useState(false)

    useDebouncedEffect(() => {
        if (isAuthenticated) {
            fetchNotes(false)
        }
    }, [isAuthenticated, fetchNotes], 200)

    const handleJoinGamma = async () => {
        if (isJoiningGamma) return
        setIsJoiningGamma(true)
        try {
            await apis.userService.joinGammaPost()
            navigate({ to: '/challenges/available' })
            handleMobileNav()
        } catch (error) {
            console.error('Failed to join Gamma:', error)
            toast.error('Failed to join. Please try again.')
        } finally {
            setIsJoiningGamma(false)
        }
    }

    const ongoingAssignments = activeAssignments.filter(assignment => assignment.slug)

    return (
      <>
        {isPartner && (
          <SideBarPartner open={open} handleMobileNav={handleMobileNav} />
        )}

        {isAdmin && (
          <SideBarAdmin open={open} handleMobileNav={handleMobileNav} />
        )}
        {/* Main Navigation Section */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <Link to="/" onClick={handleMobileNav}>
                    <Home />
                    <span>{t('common.home')}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {isAuthenticated && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link to="/user-area" onClick={handleMobileNav}>
                      <User />
                      <span>{t('common.userArea')}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {isAuthenticated && !releaseNotesBlocked && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    tooltip={t('releaseNotes.whatsNew')}
                  >
                    <Link to="/updates" onClick={handleMobileNav}>
                      <Megaphone />
                      <span>{t('releaseNotes.whatsNew')}</span>
                      {unreadCount > 0 && (
                        <SidebarMenuBadge className="border-primary/80 border rounded-xl">
                          {unreadCount}
                        </SidebarMenuBadge>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {isPerson && activeAssignments.length > 0 && (
                <SidebarMenuItem>
                  <div>
                    <SidebarMenuButton asChild>
                      <Link to="/assignments" onClick={handleMobileNav}>
                        <ClipboardList />
                        <span>{t('partner.assignments')}</span>
                        <SidebarMenuBadge className="border-primary/80 border rounded-xl">
                          {activeAssignments.length}
                        </SidebarMenuBadge>
                      </Link>
                    </SidebarMenuButton>
                  </div>
                  {ongoingAssignments.length > 0 && (
                    <SidebarMenuSub>
                      {ongoingAssignments.map(
                        (assignment) =>
                          assignment.slug && (
                            <SidebarMenuSubButton
                              key={assignment.assignmentId}
                              asChild
                            >
                              <Link
                                to="/challenge/$slug"
                                params={{ slug: assignment.slug }}
                                onClick={handleMobileNav}
                              >
                                <span>{assignment.name}</span>
                              </Link>
                            </SidebarMenuSubButton>
                          )
                      )}
                    </SidebarMenuSub>
                  )}
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                {!open ? (
                  <SidebarMenuButton asChild>
                    <Link to="/challenges/available" onClick={handleMobileNav}>
                      <Trophy />
                      <span>{t('navigation.challenges')}</span>
                    </Link>
                  </SidebarMenuButton>
                ) : (
                  <>
                    <SidebarMenuButton asChild>
                      <div className="pointer-events-none">
                        <Trophy />
                        <span>{t('navigation.challenges')}</span>
                      </div>
                    </SidebarMenuButton>
                    <SidebarMenuSub>
                      <SidebarMenuSubButton asChild>
                        <Link
                          to="/challenges/available"
                          onClick={handleMobileNav}
                        >
                          <span>{t('challenges.available_challenges_')}</span>
                        </Link>
                      </SidebarMenuSubButton>
                      <SidebarMenuSubButton asChild>
                        <Link
                          to="/challenges/completed"
                          onClick={handleMobileNav}
                        >
                          <span>{t('challenges.completed_challenges_')}</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSub>
                  </>
                )}
              </SidebarMenuItem>
              {ongoingChallenges.length > 0 && (
                <SidebarMenuItem>
                  {!open ? (
                    <SidebarMenuButton asChild>
                      <Link
                        to="/challenge/$slug"
                        params={{ slug: ongoingChallenges[0].slug }}
                        onClick={handleMobileNav}
                      >
                        <Activity />
                        <span>{t('navigation.ongoingChallenges')}</span>
                        <SidebarMenuBadge>
                          {ongoingChallenges.length}
                        </SidebarMenuBadge>
                      </Link>
                    </SidebarMenuButton>
                  ) : (
                    <>
                      <SidebarMenuButton asChild>
                        <div className="pointer-events-none">
                          <Activity />
                          <span>{t('navigation.ongoingChallenges')}</span>
                          <SidebarMenuBadge>
                            {ongoingChallenges.length}
                          </SidebarMenuBadge>
                        </div>
                      </SidebarMenuButton>
                      <SidebarMenuSub>
                        {ongoingChallenges.slice(0, 6).map((challenge) => (
                          <SidebarMenuSubButton
                            key={challenge.challengeId || challenge.slug}
                            asChild
                          >
                            <Link
                              to="/challenge/$slug"
                              params={{ slug: challenge.slug }}
                              onClick={handleMobileNav}
                            >
                              <span>{challenge.name}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        ))}
                      </SidebarMenuSub>
                    </>
                  )}
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <Link to="/about" onClick={handleMobileNav}>
                    <Info />
                    <span>{t('navigation.aboutUs')}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <Link to="/faq" onClick={handleMobileNav}>
                    <HelpCircle />
                    <span>{t('navigation.faq')}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
            {showJoinGammaCta && (
              <div
                className="px-2 mt-4"
                style={{
                  border: '3px solid transparent',
                  backgroundImage:
                    'linear-gradient(hsl(var(--color-background)), hsl(var(--color-background))), linear-gradient(to right, #6ee7b7, #047857)',
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'padding-box, border-box',
                  borderRadius: '15px',
                }}
              >
                <div className="flex items-center justify-center gap-2 py-2">
                  <Button
                    variant="link"
                    className="h-auto px-0 py-0"
                    onClick={handleJoinGamma}
                    disabled={isJoiningGamma}
                  >
                    {t('common.joinOurBeta')}
                  </Button>
                </div>
              </div>
            )}
          </SidebarGroupContent>
        </SidebarGroup>
      </>
    )
}
